import React, { useState, useEffect } from 'react';
import { CognitoUserPool, CognitoUserAttribute } from 'amazon-cognito-identity-js';
import { getUserRole, hasPermission } from '../utils/roleUtils';
import { config } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/apiRouting';

interface CognitoUserManagementProps {
  user: any;
  session?: any;
}

interface CognitoUserData {
  username: string;
  email: string;
  name?: string;
  role?: string;
  status: 'CONFIRMED' | 'UNCONFIRMED' | 'FORCE_CHANGE_PASSWORD' | 'DISABLED';
  enabled: boolean;
  createdDate?: string;
  lastModifiedDate?: string;
  employeeRecord?: {
    id: string;
    firstName: string;
    lastName: string;
    position: string;
    department: string;
    hireDate: string;
  };
}

interface NewUserForm {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  position: string;
  department: string;
  tempPassword: string;
  enterpriseId: string;
  storeId?: string;
}

const CognitoUserManagement: React.FC<CognitoUserManagementProps> = ({ user, session }) => {
  const { getIdToken } = useAuth();
  const [cognitoUsers, setCognitoUsers] = useState<CognitoUserData[]>([]);
  const [enterprises, setEnterprises] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEnterprises, setLoadingEnterprises] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState<NewUserForm>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'employee',
    position: '',
    department: '',
    tempPassword: '',
    enterpriseId: '',
    storeId: ''
  });

  const userPool = new CognitoUserPool({
    UserPoolId: config.COGNITO_USER_POOL_ID,
    ClientId: config.COGNITO_CLIENT_ID,
  });

  // Use centralized role management system
  const userRole = getUserRole(user, session);
  const isAdmin = hasPermission(user, 'canManageUsers', session);
  const isManager = userRole === 'manager';

  useEffect(() => {
    if (isAdmin || isManager) {
      loadUsers();
      loadEnterprises();
    }
  }, [isAdmin, isManager]);

  const getCurrentUserEnterpriseId = (): string => {
    // Extract enterprise ID from current user's context
    if (session && session.getIdToken()) {
      const payload = session.getIdToken().payload;
      return payload['custom:enterprise_id'] || payload.enterpriseId || '';
    }
    return '';
  };

  const loadEnterprises = async () => {
    try {
      setLoadingEnterprises(true);
      console.log('🏢 Loading enterprises for dropdown...');
      
      const response = await fetch(API_ENDPOINTS.enterprises(), {
        headers: {
          'Authorization': `Bearer ${await getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Handle both array response and {data: []} wrapped response
        const enterpriseList = Array.isArray(data) ? data : (data.data || data.enterprises || []);
        setEnterprises(enterpriseList);

        // Auto-populate enterprise for non-admin users
        if (!isAdmin) {
          const userEnterpriseId = getCurrentUserEnterpriseId();
          if (userEnterpriseId) {
            setNewUser(prev => ({ ...prev, enterpriseId: userEnterpriseId }));
          }
        }
      } else {
        console.error('❌ Failed to load enterprises');
        setError('Failed to load enterprises');
      }
    } catch (err) {
      console.error('❌ Error loading enterprises:', err);
      setError('Network error while loading enterprises');
    } finally {
      setLoadingEnterprises(false);
    }
  };

  const loadStoresForEnterprise = async (enterpriseId: string) => {
    if (!enterpriseId) {
      setStores([]);
      return;
    }

    try {
      console.log('🏪 Loading stores for enterprise:', enterpriseId);
      
      const response = await fetch(`${API_ENDPOINTS.stores()}?enterpriseId=${enterpriseId}`, {
        headers: {
          'Authorization': `Bearer ${await getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Handle both array response and {data: []} wrapped response
        const storeList = Array.isArray(data) ? data : (data.data || data.stores || []);
        console.log('✅ Stores loaded:', storeList.length);
        setStores(storeList);
      } else {
        console.error('❌ Failed to load stores for enterprise');
        setStores([]);
      }
    } catch (err) {
      console.error('❌ Error loading stores:', err);
      setStores([]);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      console.log('🚀 Loading Cognito users and employee data...');

      // Note: In a production app, you'd typically use AWS Lambda with AdminSDK
      // to list users since browser can't directly call Cognito Admin APIs
      // For now, we'll load employee records and match them to current user info
      
      const response = await fetch(API_ENDPOINTS.employees(), {
        headers: {
          'Authorization': `Bearer ${await getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Employee data received:', data);

        // Handle both array response and {data: []} wrapped response
        const employeeList = Array.isArray(data) ? data : (data.data || data.employees || []);

        // Transform employee data to include Cognito user info
        const transformedUsers: CognitoUserData[] = employeeList.map((emp: any) => ({
          username: emp.email,
          email: emp.email,
          name: `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
          role: emp.role || 'employee',
          status: emp.status === 'active' ? 'CONFIRMED' : 'DISABLED',
          enabled: emp.status === 'active',
          createdDate: emp.createdAt || emp.hireDate,
          employeeRecord: {
            id: emp.employeeId || emp.id,
            firstName: emp.firstName,
            lastName: emp.lastName,
            position: emp.employeePositions?.[0]?.positionTitle || 'Employee',
            department: emp.employeePositions?.[0]?.department || 'General',
            hireDate: emp.hireDate || emp.createdAt
          }
        }));

        setCognitoUsers(transformedUsers);
        console.log('👥 Users loaded:', transformedUsers.length);
      } else {
        console.error('❌ Failed to load employee data');
        setError('Failed to load user data');
      }
    } catch (err) {
      console.error('❌ Error loading users:', err);
      setError('Network error while loading users');
    } finally {
      setLoading(false);
    }
  };

  const generateTempPassword = () => {
    // Generate a secure temporary password
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    
    // Ensure at least one character from each required category
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // Uppercase
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // Lowercase
    password += '0123456789'[Math.floor(Math.random() * 10)]; // Number
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)]; // Special char
    
    // Fill remaining characters
    for (let i = 4; i < 12; i++) {
      password += chars[Math.floor(Math.random() * chars.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const createCognitoUser = async (userData: NewUserForm): Promise<string> => {
    return new Promise((resolve, reject) => {
      const tempPassword = userData.tempPassword || generateTempPassword();
      
      const attributeList = [
        new CognitoUserAttribute({
          Name: 'email',
          Value: userData.email,
        }),
        new CognitoUserAttribute({
          Name: 'name',
          Value: `${userData.firstName} ${userData.lastName}`,
        }),
        new CognitoUserAttribute({
          Name: 'custom:role',
          Value: userData.role,
        }),
        new CognitoUserAttribute({
          Name: 'custom:enterprise_id',
          Value: userData.enterpriseId,
        }),
      ];

      if (userData.storeId) {
        attributeList.push(new CognitoUserAttribute({
          Name: 'custom:store_id',
          Value: userData.storeId,
        }));
      }

      console.log('🚀 Creating Cognito user:', userData.email);

      userPool.signUp(
        userData.email, // username
        tempPassword, // temporary password
        attributeList,
        [], // validation data
        (err, result) => {
          if (err) {
            console.error('❌ Cognito user creation failed:', err);
            reject(err);
            return;
          }
          
          console.log('✅ Cognito user created:', result?.user.getUsername());
          resolve(tempPassword);
        }
      );
    });
  };

  const createEmployeeRecord = async (userData: NewUserForm): Promise<any> => {
    const employeeData = {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      role: userData.role,
      enterpriseId: userData.enterpriseId,
      storeId: userData.storeId || undefined,
      status: 'active',
      hireDate: new Date().toISOString().split('T')[0],
      positions: [{
        positionTitle: userData.position,
        department: userData.department || 'General',
        payRate: 0,
        payType: 'hourly',
        isPrimary: true
      }]
    };

    console.log('🚀 Creating employee record:', employeeData);

    const response = await fetch(API_ENDPOINTS.employees(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getIdToken()}`
      },
      body: JSON.stringify(employeeData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create employee record');
    }

    const result = await response.json();
    console.log('✅ Employee record created:', result.data);
    return result.data;
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      console.log('🚀 Starting dual user creation process...');
      
      // Step 1: Create Cognito user
      const tempPassword = await createCognitoUser(newUser);
      console.log('✅ Step 1 complete: Cognito user created');
      
      // Step 2: Create employee record
      const employeeRecord = await createEmployeeRecord(newUser);
      console.log('✅ Step 2 complete: Employee record created');
      
      // Step 3: Add to local state
      const newUserData: CognitoUserData = {
        username: newUser.email,
        email: newUser.email,
        name: `${newUser.firstName} ${newUser.lastName}`,
        role: newUser.role,
        status: 'FORCE_CHANGE_PASSWORD',
        enabled: true,
        createdDate: new Date().toISOString(),
        employeeRecord: {
          id: employeeRecord.employeeId || employeeRecord.id,
          firstName: newUser.firstName,
          lastName: newUser.lastName,
          position: newUser.position,
          department: newUser.department,
          hireDate: employeeRecord.hireDate
        }
      };

      setCognitoUsers([...cognitoUsers, newUserData]);
      
      setMessage(
        `✅ User ${newUser.email} created successfully!\n` +
        `🔑 Temporary password: ${tempPassword}\n` +
        `📧 User will be required to change password on first login.`
      );
      
      // Reset form
      const resetEnterpriseId = isAdmin ? '' : getCurrentUserEnterpriseId(); // Keep enterprise for managers
      setNewUser({
        email: '',
        firstName: '',
        lastName: '',
        role: 'employee',
        position: '',
        department: '',
        tempPassword: '',
        enterpriseId: resetEnterpriseId,
        storeId: ''
      });
      setStores([]); // Clear stores when resetting
      setShowAddForm(false);
      
    } catch (err: any) {
      console.error('❌ User creation failed:', err);
      setError(`Failed to create user: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (username: string) => {
    try {
      const targetUser = cognitoUsers.find(u => u.username === username);
      if (!targetUser?.employeeRecord) return;

      const newStatus = targetUser.enabled ? 'inactive' : 'active';
      console.log('🔄 Updating user status:', username, 'to', newStatus);

      // Update employee record status
      const response = await fetch(API_ENDPOINTS.employee(targetUser.employeeRecord.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getIdToken()}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        // Update local state
        setCognitoUsers(cognitoUsers.map(u => 
          u.username === username 
            ? { 
                ...u, 
                enabled: !u.enabled,
                status: newStatus === 'active' ? 'CONFIRMED' : 'DISABLED'
              }
            : u
        ));
        console.log('✅ User status updated successfully');
        
        // Note: In production, you'd also disable/enable the Cognito user
        // using AWS Lambda with AdminSDK (AdminDisableUser/AdminEnableUser)
        
      } else {
        const errorData = await response.json();
        setError('Failed to update user status: ' + (errorData.error || 'Unknown error'));
      }
    } catch (err: any) {
      console.error('❌ Error updating user status:', err);
      setError('Network error while updating user status');
    }
  };

  const resendTempPassword = async (username: string) => {
    try {
      console.log('🔄 Resending temporary password for:', username);
      
      // Generate new temp password
      const newTempPassword = generateTempPassword();
      
      // Note: In production, you'd use AWS Lambda with AdminSDK
      // to call AdminSetUserPassword and AdminInitiateAuth
      
      setMessage(`🔑 New temporary password for ${username}: ${newTempPassword}`);
      
    } catch (err: any) {
      console.error('❌ Error resending password:', err);
      setError('Failed to resend temporary password');
    }
  };

  if (loading) {
    return <div>Loading user management...</div>;
  }

  if (!isAdmin && !isManager) {
    return (
      <div>
        <div className="dashboard-card">
          <h3>Access Denied</h3>
          <p>Only administrators and managers can manage users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management-container">
      <div className="dashboard-card" style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2>Cognito User Management</h2>
            <p style={{ margin: 0 }}>Manage authentication users and their employee records</p>
          </div>
          <div>
            <button 
              className="btn-modern-accent"
              onClick={() => {
                setShowAddForm(true);
                // Auto-populate and load stores if manager has fixed enterprise
                if (!isAdmin) {
                  const userEnterpriseId = getCurrentUserEnterpriseId();
                  if (userEnterpriseId) {
                    setNewUser(prev => ({ ...prev, enterpriseId: userEnterpriseId }));
                    loadStoresForEnterprise(userEnterpriseId);
                  }
                }
              }}
            >
              Add User
            </button>
          </div>
        </div>
      </div>

      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-value">{cognitoUsers.length}</div>
          <div className="metric-label">Total Users</div>
        </div>
        <div className="metric-card">
          <div className="metric-value">{cognitoUsers.filter(u => u.enabled).length}</div>
          <div className="metric-label">Active Users</div>
        </div>
        <div className="metric-card">
          <div className="metric-value">{cognitoUsers.filter(u => u.role === 'manager').length}</div>
          <div className="metric-label">Managers</div>
        </div>
        <div className="metric-card">
          <div className="metric-value">{cognitoUsers.filter(u => u.status === 'FORCE_CHANGE_PASSWORD').length}</div>
          <div className="metric-label">Pending Password Change</div>
        </div>
      </div>

      {message && (
        <div className="dashboard-card" style={{ border: '1px solid #10b981', backgroundColor: '#d1fae5', marginBottom: '32px' }}>
          <pre style={{ color: '#065f46', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{message}</pre>
        </div>
      )}

      {error && (
        <div className="dashboard-card" style={{ border: '1px solid #dc3545', backgroundColor: '#f8d7da', marginBottom: '32px' }}>
          <p style={{ color: '#721c24', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Users Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Role & Position</th>
              <th>Cognito Status</th>
              <th>Employee Status</th>
              <th>Hire Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {cognitoUsers.map((userData) => (
              <tr key={userData.username}>
                <td>
                  <div>
                    <div style={{ fontWeight: '600', color: '#212529' }}>{userData.name}</div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>Username: {userData.username}</div>
                  </div>
                </td>
                <td style={{ color: '#495057' }}>{userData.email}</td>
                <td>
                  <div>
                    <div style={{ fontWeight: '500', color: '#212529' }}>{userData.role}</div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      {userData.employeeRecord?.position} - {userData.employeeRecord?.department}
                    </div>
                  </div>
                </td>
                <td>
                  <span className={`status-badge ${userData.status.toLowerCase()}`}>
                    {userData.status}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${userData.enabled ? 'active' : 'inactive'}`}>
                    {userData.enabled ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td style={{ color: '#495057' }}>
                  {userData.employeeRecord?.hireDate ? 
                    new Date(userData.employeeRecord.hireDate).toLocaleDateString() : 
                    'N/A'
                  }
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button 
                      className="btn-modern-secondary" 
                      style={{ padding: '6px 12px', fontSize: '12px' }}
                      onClick={() => toggleUserStatus(userData.username)}
                    >
                      {userData.enabled ? 'Disable' : 'Enable'}
                    </button>
                    {userData.status === 'FORCE_CHANGE_PASSWORD' && (
                      <button 
                        className="btn-modern-secondary" 
                        style={{ padding: '6px 12px', fontSize: '12px' }}
                        onClick={() => resendTempPassword(userData.username)}
                      >
                        Resend Password
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add User Modal */}
      {showAddForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New User</h3>
            </div>
            <div className="modal-body">
              <form id="user-form" onSubmit={handleCreateUser}>
                <div className="form-group">
                  <label className="label-modern">Email Address *</label>
                  <input
                    type="email"
                    className="input-modern"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="user@bigsystemone.com"
                    required
                  />
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label className="label-modern">First Name *</label>
                    <input
                      type="text"
                      className="input-modern"
                      value={newUser.firstName}
                      onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                      placeholder="John"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="label-modern">Last Name *</label>
                    <input
                      type="text"
                      className="input-modern"
                      value={newUser.lastName}
                      onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                      placeholder="Doe"
                      required
                    />
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label className="label-modern">Role *</label>
                    <select
                      className="input-modern"
                      value={newUser.role}
                      onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    >
                      <option value="employee">Employee</option>
                      {isAdmin && <option value="manager">Manager</option>}
                      {isAdmin && <option value="admin">Admin</option>}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label className="label-modern">Position *</label>
                    <input
                      type="text"
                      className="input-modern"
                      value={newUser.position}
                      onChange={(e) => setNewUser({ ...newUser, position: e.target.value })}
                      placeholder="Server, Cook, Manager, etc."
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="label-modern">Department</label>
                  <input
                    type="text"
                    className="input-modern"
                    value={newUser.department}
                    onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                    placeholder="Kitchen, Front of House, Management"
                  />
                </div>
                
                <div className="form-group">
                  <label className="label-modern">Enterprise *</label>
                  {loadingEnterprises ? (
                    <div className="input-modern" style={{ display: 'flex', alignItems: 'center', color: '#6c757d' }}>
                      Loading enterprises...
                    </div>
                  ) : (
                    <select
                      className="input-modern"
                      value={newUser.enterpriseId}
                      onChange={(e) => {
                        const enterpriseId = e.target.value;
                        setNewUser({ ...newUser, enterpriseId, storeId: '' }); // Reset store when enterprise changes
                        loadStoresForEnterprise(enterpriseId); // Load stores for selected enterprise
                      }}
                      disabled={!isAdmin && !!getCurrentUserEnterpriseId()} // Disable if manager with fixed enterprise
                      required
                    >
                      <option value="">Select Enterprise</option>
                      {enterprises.map((enterprise) => (
                        <option key={enterprise.enterpriseId} value={enterprise.enterpriseId}>
                          {enterprise.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {!isAdmin && getCurrentUserEnterpriseId() && (
                    <small style={{ color: '#6c757d' }}>
                      🔒 Managers can only create users for their enterprise
                    </small>
                  )}
                  {isAdmin && (
                    <small style={{ color: '#6c757d' }}>
                      💡 Select the enterprise this user will belong to
                    </small>
                  )}
                </div>
                
                <div className="form-group">
                  <label className="label-modern">Store (Optional)</label>
                  <select
                    className="input-modern"
                    value={newUser.storeId}
                    onChange={(e) => setNewUser({ ...newUser, storeId: e.target.value })}
                    disabled={!newUser.enterpriseId}
                  >
                    <option value="">No specific store (Enterprise-level user)</option>
                    {stores.map((store) => (
                      <option key={store.storeId} value={store.storeId}>
                        {store.name} - {store.address}
                      </option>
                    ))}
                  </select>
                  <small style={{ color: '#6c757d' }}>
                    {!newUser.enterpriseId 
                      ? 'Select an enterprise first to see available stores'
                      : stores.length === 0 
                        ? 'No stores found for this enterprise'
                        : 'Leave empty for enterprise-level access, or select a specific store'
                    }
                  </small>
                </div>
                
                <div className="form-group">
                  <label className="label-modern">Temporary Password (Optional)</label>
                  <input
                    type="text"
                    className="input-modern"
                    value={newUser.tempPassword}
                    onChange={(e) => setNewUser({ ...newUser, tempPassword: e.target.value })}
                    placeholder="Leave empty to auto-generate"
                  />
                  <small style={{ color: '#6c757d' }}>
                    User will be required to change password on first login
                  </small>
                </div>
              </form>
            </div>
            
            <div className="modal-footer">
              <button type="submit" className="btn-modern-primary" disabled={loading} form="user-form">
                {loading ? 'Creating User...' : 'Create User & Employee Record'}
              </button>
              <button 
                type="button" 
                className="btn-modern-secondary"
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Information Cards */}
      <div className="dashboard-grid" style={{ marginTop: '32px' }}>
        <div className="dashboard-card">
          <h3>🔐 Cognito Integration</h3>
          <p>This system creates both Cognito users (for authentication) and employee records (for business data).</p>
          <ul className="feature-list">
            <li>Cognito User Pool for authentication</li>
            <li>Employee records in DynamoDB</li>
            <li>Linked by email address</li>
            <li>Role-based access control</li>
          </ul>
        </div>
        
        <div className="dashboard-card">
          <h3>👤 User Creation Process</h3>
          <p>Creating a new user involves multiple steps:</p>
          <ul className="feature-list">
            <li>1. Select enterprise and store from dropdowns</li>
            <li>2. Create Cognito user with temp password</li>
            <li>3. Create employee record in DynamoDB</li>
            <li>4. Link via email address</li>
            <li>5. User must change password on first login</li>
          </ul>
        </div>
        
        <div className="dashboard-card">
          <h3>🔒 Security Features</h3>
          <p>Built-in security and compliance:</p>
          <ul className="feature-list">
            <li>Force password change on first login</li>
            <li>AWS Cognito password policies</li>
            <li>JWT token authentication</li>
            <li>Role and permission enforcement</li>
          </ul>
        </div>
        
        <div className="dashboard-card">
          <h3>⚠️ Production Notes</h3>
          <p>Additional features needed for production:</p>
          <ul className="feature-list">
            <li>AWS Lambda for Cognito Admin APIs</li>
            <li>Email notifications for new users</li>
            <li>Password reset workflows</li>
            <li>User activity logging</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CognitoUserManagement;